import { Link } from "react-router-dom";
import { DashboardKeyMetrics } from "../components/dashboard/DashboardKeyMetrics";
import { TelanganaDistrictMap } from "../components/dashboard/TelanganaDistrictMap";
import { useAppState } from "../context/AppStateContext";
import { computeKpis } from "../kpi";

export function Dashboard() {
  const { applications } = useAppState();
  const k = computeKpis(applications);
  const total = Math.max(k.totalReceived, 1);
  const pct = (n: number) => Math.round((n / total) * 100);

  const stages = [
    { key: "hold", label: "On hold", count: k.onHold },
    { key: "pend", label: "HO verification pending", count: k.pendingValidation },
    { key: "ready", label: "Ready for ICICI", count: k.readyForIcici },
    { key: "mou", label: "Ready for MOU", count: k.readyForMou },
    { key: "bank", label: "Awaiting subcontractor", count: k.awaitingSubcontractor },
    { key: "done", label: "Subcontractor linked", count: k.subcontractorLinked },
  ] as const;

  return (
    <div className="dashboard-corp">
      <TelanganaDistrictMap applications={applications} className="dash-corp-map--first" />

      <DashboardKeyMetrics applications={applications} />

      <section className="dash-corp-executive" aria-label="Portfolio and process summary">
        <div className="dash-corp-panel dash-corp-panel--primary">
          <header className="dash-corp-panel__head">
            <div>
              <p className="dash-corp-eyebrow">Applicant volume</p>
              <p className="dash-corp-stat-xl" aria-live="polite">
                {k.totalReceived}
              </p>
              <p className="dash-corp-panel__lede">Total dossiers currently in the demonstration applicant list.</p>
            </div>
          </header>

          <p className="dash-corp-bar-label">Distribution by processing stage</p>
          <div className="dash-corp-bar" role="img" aria-label="Case distribution by stage">
            {stages.map((row) => (
              <span
                key={row.key}
                className={`dash-corp-bar__seg dash-corp-bar__seg--${row.key}`}
                style={{ width: `${pct(row.count)}%` }}
                title={row.label}
              />
            ))}
          </div>

          <div className="dash-corp-legend" role="list">
            {stages.map((row) => (
              <div key={row.key} className="dash-corp-legend__row" role="listitem">
                <span className={`dash-corp-legend__swatch dash-corp-legend__swatch--${row.key}`} />
                <span className="dash-corp-legend__label">{row.label}</span>
                <span className="dash-corp-legend__count">{row.count}</span>
                <span className="dash-corp-legend__pct">{pct(row.count)}%</span>
              </div>
            ))}
          </div>
        </div>

        <aside className="dash-corp-panel dash-corp-panel--side" aria-label="Standard operating sequence">
          <h2 className="dash-corp-panel__title">Standard workflow</h2>
          <p className="dash-corp-panel__intro">
            End-to-end responsibility from field capture through HO validation and subcontractor registration.
          </p>
          <ol className="dash-corp-process">
            <li>Field intake via mobile application.</li>
            <li>HO verification of identity, bank, and workman documentation; holds where required.</li>
            <li>Aadhaar seeding confirmation prior to ICICI and MOU capture.</li>
            <li>Work-done bills with seeding re-verification at site.</li>
            <li>Subcontractor firm linkage to close the file.</li>
          </ol>
          <Link to="/applications" className="btn btn--corp btn--corp-outline dash-corp-panel__cta">
            View applicants
          </Link>
        </aside>
      </section>

      <section className="dash-corp-quickstart" aria-label="Getting started">
        <h2 className="dash-corp-section-title">Getting started</h2>
        <div className="dash-corp-quickstart__grid">
          <div className="dash-corp-quickstart__card">
            <span className="dash-corp-quickstart__step">01</span>
            <p className="dash-corp-quickstart__text">
              Open <Link to="/applications">Applicants</Link> from the main menu.
            </p>
          </div>
          <div className="dash-corp-quickstart__card">
            <span className="dash-corp-quickstart__step">02</span>
            <p className="dash-corp-quickstart__text">
              Select <strong>Open</strong> on any row to open the workspace.
            </p>
          </div>
          <div className="dash-corp-quickstart__card">
            <span className="dash-corp-quickstart__step">03</span>
            <p className="dash-corp-quickstart__text">
              Use the left navigation to complete each section in order.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
